import type { FunctionComponent } from 'react'
import React from 'react'
import {
  CardContainer,
  Picture,
  TooltipWithButton,
  TypographyV2 as Typography,
} from '@which/seatbelt'

import classnames from 'classnames'

import type { PicksProductCard } from '../../../../generated/frontend'
import { AffiliateLinksList } from '..'
import styles from './Card.module.scss'

export const Card: FunctionComponent<Props> = ({
  description,
  model,
  manufacturer,
  title,
  affiliateItems,
  image,
  className,
  reviewUrl,
  typicalPrice,
  usesPimData,
  articleLink,
  articleLinkLabel,
  annualRunningCost,
}) => {
  const includesPapiData = Boolean(model && manufacturer && typicalPrice)

  if (includesPapiData && affiliateItems?.length === 0) {
    return null
  }

  return (
    <div className={classnames(styles.shopCardWrapper, className)}>
      <CardContainer>
        {image ? (
          <div className={styles.shopCardPictureContainer}>
            <Picture
              alt={image.alt}
              src={image.src}
              sources={image.sources}
              className={styles.shopCardImage}
              aspectRatioMain="four-to-three"
              width="200"
              height="150"
            />
            {annualRunningCost && (
              <div className={styles.shopCardRunningCost}>
                <Typography tag="span" textStyle="sb-text-interface-body-small-regular">
                  {annualRunningCost.formatted} to run per year
                </Typography>
                <TooltipWithButton
                  title={annualRunningCost.label}
                  ariaLabel={annualRunningCost.label}
                  contents="An estimated running cost based on typical usage. Prices are based on a unit of electricity costing 27.35p per kWh (October 2023)."
                  isTooltipDisplayedAboveOverride={false}
                />
              </div>
            )}
          </div>
        ) : (
          <span className={styles.shopCardMissingImage}>
            <Typography tag="span" textStyle="sb-text-interface-body-small-regular">
              Missing image
            </Typography>
          </span>
        )}
        <div className={styles.shopCardDetails}>
          <Typography tag="h3" textStyle="sb-text-heading-small">
            {title}
          </Typography>
          {model && (
            <Typography tag="h4" textStyle="sb-text-body-default-regular">
              {model}
            </Typography>
          )}
          <Typography textStyle="sb-text-body-default-regular">{description}</Typography>
        </div>
        {affiliateItems?.length > 0 && (
          <AffiliateLinksList
            affiliateItems={affiliateItems}
            manufacturer={manufacturer}
            model={model}
          />
        )}
        {!includesPapiData && (
          <div className={styles.noProductDataText}>
            <Typography tag="span" textStyle="sb-text-interface-body-small-regular">
              Pricing and offers information are currently unavailable
            </Typography>
          </div>
        )}
        <div className={styles.shopCardFooter}>
          {usesPimData ? (
            <a
              href={reviewUrl}
              className={classnames('sb-link-primary', styles.shopCardReviewLink)}
              data-which-id="wpicks-full-review-link"
              data-testid="wpicks-full-review-link"
            >
              <span className="sb-link-animation-wrapper">Full review</span>
            </a>
          ) : (
            <>
              <Typography
                textStyle="sb-text-body-small-regular"
                className={styles.shopCardFooterPrice}
              >
                Typical price {typicalPrice}
              </Typography>

              {articleLink && (
                <a
                  href={articleLink}
                  className={classnames('sb-link-primary', styles.shopCardArticleLink)}
                  data-which-id="wpicks-article-link"
                  data-testid="wpicks-article-link"
                >
                  <span className="sb-link-animation-wrapper">
                    {articleLinkLabel ?? 'Read article'}
                  </span>
                </a>
              )}
            </>
          )}
        </div>
      </CardContainer>
    </div>
  )
}

///////// IMPLEMENTATION /////////

export type Props = Omit<
  PicksProductCard,
  'reviewUrl' | 'articleLink' | 'articleLinkLabel' | 'annualRunningCost'
> & {
  reviewUrl?: string
  articleLink?: string
  articleLinkLabel?: string
  annualRunningCost?: PicksProductCard['annualRunningCost'] | null
  className?: string
}
